import BoardTool from '@/components/common/ed-board/ed-board-tools/board-tool.interface';
import WboDrawerConfig from './wbo-drawer-config.interface';
import {WboBoardData} from '@/components/common/ed-board/ed-board-tools/ed-board-persistence/persistent-board-data';
import EdWboDrawerStackFunctions
    from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/undo-redo/ed-wbo-drawer-stack-functions';
import {BOARD_CONFIG_PARAMETERS} from '@/values/ed-whiteboard.values';
import {
    CursorStyle,
    OthersCursorMode
} from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/wbo-drawer-config.enum';

export default class WboDrawerTools {
    public scaleTimeout: NodeJS.Timeout = null;

    /**
     * Whether the user is host in PM or not.
     */
    isHostUser?: boolean;

    /**
     * Username of the current user. This user will own all the annotations drawn by him.
     */
    username: string;

    /**
     * Current user's full name
     */
    userFullName?: string;

    /**
     * The registered tools. Each tool registers itself in this object.
     */
    list?: {[index: string]: BoardTool};

    /**
     * A handle to the root SVG element in which the WboDrawer will draw.
     */
    svg?: SVGSVGElement;

    /**
     * A handle to actual <g></g> element inside the root SVG element, where the actual drawing will take place.
     */
    drawingArea?: SVGGElement;

    /**
     * The container element for the board. This is actually needed just to get the dimensions of the board.
     */
    board?: HTMLDivElement;

    /**
     * The container element for all the boards. This is the one that controls the scroll over all the boards.
     */
    pageContainer?: HTMLDivElement;

    cursors?: SVGGElement;

    /**
     * The scale of the root SVG element.
     */
    scale?: number;

    /**
     * Scale of the Y axis for the weird scenarios in which the board is rescaled without keeping the aspect ratio.
     */
    yScale?: number;

    /**
     * An array with handlers that react to size changes.
     */
    sizeChangeHandlers?: Function[];

    /**
     * The currently active tool.
     */
    curTool?: BoardTool;


    drawingEvent?: boolean;

    showMarker?: boolean;

    showOtherCursors?: boolean;

    othersCursorStyle?: CursorStyle;

    showMyCursor?: boolean;

    alwaysShowCursors?: boolean;

    othersCursorMode?: OthersCursorMode;

    /**
     * Whether the browser is IE or not.
     */
    isIE?: boolean;

    /**
     * A key/value object of configuration entries directly related with the board itself. For instance,
     * 'wboMaxEmitCountPeriod' refers to quantity of
     */
    boardParameters?: any;

    /**
     * A map that will hold the owner of each annotation.
     * Key: annotationId.
     * Value: username of the owner of the annotation.
     */
    annotationOwners?: {[index: string]: {userName: string}};

    /**
     * The size of the stroke that is currently selected.
     */
    size?: number;

    /**
     * Percentage of opacity of the stroke.
     */
    opacity?: number;

    /**
     * Current stroke color.
     */
    color?: string;

    /**
     * An array of the annotations that this drawer has drawn.
     */
    annotations?: string[];

    /**
     * Array of messages pending to be drawn.
     */
    pendingMessages?: any;

    /**
     * Optional list of hook functions that will be applied to the messages before sending or drawing them
     */
    messageHooks?: Function[];

    /**
     * Optional list of hook functions that will be applied to tools before adding them
     */
    toolHooks?: Function[];

    /**
    * Optional function to call before a tool draws.
    */
    beforeDrawCallback?: Function;

    /**
     * Optional function to call right after a tool draws.
     */
    drawCallback?: Function;

    /**
     * Optional function to call right before send.
     */
    sendCallback?: Function;

    /**
     * Optional function to call whenever a stroke is stopped.
     */
    stopStrokeCallback?: Function;

    /**
     * It references the component's instance which holds and stores all the annotations being made locally.
     */
    persistentBoard: WboBoardData;

    /**
     * It contains the name of the current board
     */
    currentBoard: string;

    /**
    * It defines the basics functions over the undo-redo stack allocated in the store
    */
    undoRedoStackFunctions: EdWboDrawerStackFunctions;

    persistenceFunction?: Function;

    /**
     * Function to identify a board with current work
     */
    updateBoardWorkInProgress: (workInProgress: boolean) => void = () => {};
    /**
     * Function to send a message without saving data locally
     */
    broadcastMessage: Function;
    /**
     * Function to determinate if can send a message through the socket
     */
    canSendMessage: Function;

    constructor(config: WboDrawerConfig) {
        this.list = {};
        this.isHostUser = config.isHostUser;
        this.username = config.username;
        this.userFullName = config.userFullName;
        this.scale = config.scale;
        this.sizeChangeHandlers = [];
        this.curTool = config.curTool;
        this.drawingEvent = config.drawingEvent;
        this.cursors = config.cursors;
        this.showMarker = config.showMarker;
        this.showOtherCursors = config.showOtherCursors;
        this.othersCursorStyle = config.othersCursorStyle;
        this.othersCursorMode = config.othersCursorMode;
        this.showMyCursor = config.showMyCursor;
        this.isIE = config.isIE;
        this.svg = config.svg;
        this.drawingArea = config.drawingArea;
        this.board = config.board;
        this.pageContainer = config.pageContainer;
        this.annotationOwners = {};
        this.size = config.size;
        this.opacity = config.opacity;
        this.annotations = config.annotations;
        this.boardParameters = config.boardParameters;
        this.toolHooks = config.toolHooks ?? [];
        this.messageHooks = config.messageHooks ?? [];
        this.pendingMessages = {};
        this.persistentBoard = config.persistentBoard;
        this.currentBoard = config.currentBoard;
        this.undoRedoStackFunctions = config.undoRedoStackFunctions;
        this.persistenceFunction = config.persistenceFunction;
        this.alwaysShowCursors = config.alwaysShowCursors;
    }

    applyHooks(hooks: any[], object): void {
        //Apply every hooks on the object
        hooks.forEach(hook => hook(object));
    }

    isBlocked(tool: BoardTool): boolean {
        return false;
    }

    register(newTool: BoardTool): void {
        if (this.isBlocked(newTool)) {
            return;
        }

        //Format the new tool correctly
        this.applyHooks(this.toolHooks, newTool);

        //Add the tool to the list
        this.list[newTool.name] = newTool;

        // Register the change handlers
        if (newTool.onSizeChange) {
            this.sizeChangeHandlers.push(newTool.onSizeChange);
        }

        //There may be pending messages for the tool
        let pending = this.pendingMessages[newTool.name];
        if (pending) {
            while (pending.length > 0) {
                let msg = pending.shift();
                //Transmit the message to the tool (precising that it comes from the network)
                newTool.draw(msg, false, this);
            }
        }
    }

    public add(newTool: BoardTool) {
        if (this.isBlocked(newTool)) {
            return;
        }

        this.register(newTool);
    }

    public generateUID(prefix: string, suffix?: string): string {
        let uid = Date.now().toString(36); //Create the uids in chronological order
        uid += (Math.round(Math.random() * 36)).toString(36); //Add a random character at the end
        if (prefix) {
            uid = prefix + uid;
        }
        if (suffix) {
            uid = uid + suffix;
        }
        return uid;
    }

    public createSVGElement(name: string, attrs?: any): Element {
        let elem = document.createElementNS(this.svg.namespaceURI, name);
        if (typeof (attrs) !== 'object') {
            return elem;
        }
        Object.keys(attrs).forEach((key, i) => {
            elem.setAttributeNS(null, key, attrs[key]);
        });
        return elem;
    }

    public addToolListeners(tool: BoardTool): void {
        for (let event in tool.compiledListeners) {
            let listener = tool.compiledListeners[event];
            let target = listener.target || this.board;
            if (event === 'mouseleave') {
                // Mouseleave events are targeted directly to the svg so the outside points are in the same scale, so we
                // can end the stroke smoothly.  If we let this event be targeted to this.board, the outside points is
                // in a totally different scale which causes either an ugly and disruptive stroke.
                target = this.svg || this.board;
            }
            target.addEventListener(event, listener, {'passive': false});
        }
    }


    public removeToolListeners(tool: BoardTool): void {
        if(tool) {
            for (let event in tool.compiledListeners) {
                const listener = tool.compiledListeners[event];
                const target = listener.target || this.board;
                target.removeEventListener(event, listener);
                // also attempt to remove with capture = true in IE
                if (this.isIE) {
                    target.removeEventListener(event, listener, true);
                }
            }
        }   
    }

    public setSize(value: number) {
        this.size = Math.max(1, Math.min(50, value | 0));
        this.sizeChangeHandlers.forEach((handler) => {
            handler(this.size);
        });
    }

    public getSize(): number {
        return this.size;
    }

    public getColor(): string {
        return this.color;
    }

    public getOpacity(): number {
        return Math.max(0.1, Math.min(1, this.opacity));
    }

    public getPersistentBoardData(): WboBoardData {
        return this.persistentBoard;
    }

    public getCurrentBoard(): string {
        return this.currentBoard;
    }

    public getUndoRedoStackFunctions(): EdWboDrawerStackFunctions {
        return this.undoRedoStackFunctions;
    }

    public getScale(): number {
        return this.scale;
    }

    public getYScale(): number {
        return this.yScale || this.scale;
    }

    public setScale(scale: number, yScale?: number) {
        const fullScale = Math.max(window.innerWidth, window.innerHeight) / this.boardParameters[BOARD_CONFIG_PARAMETERS.MAX_BOARD_SIZE];
        const minScale = Math.max(0.1, fullScale);
        const maxScale = 10;

        if (isNaN(scale)) {
            scale = 1;
        }
        scale = Math.max(minScale, Math.min(maxScale, scale));

        this.svg.style.willChange = 'transform';
        this.svg.style.transform = yScale ? `scale(${scale}, ${yScale})` : `scale(${scale})`;
        clearTimeout(this.scaleTimeout);
        this.scaleTimeout = setTimeout(() => this.svg.style.willChange = 'auto', 1000);
        this.scale = scale;
        this.yScale = yScale;
        return scale;
    }

    public drawAndSend(data: any, selectedTool?: any, isMadeByUser: boolean = true): void {
        const tool = selectedTool || this.curTool;
        data.tool = tool.name;
        if (tool.name === 'Cursor') {
            data.user = this.userFullName;
        } else {
            this.addOwner(data);
        }
        if (this.draw(tool, data, isMadeByUser)) {
            this.send(tool, data, isMadeByUser);
        }

        if (data.type === 'delete') {
            this.removeAnnotationId(data.id);
            return;
        }
        this.addAnnotationId(data.id, data.userName);
    }

    private draw(tool: BoardTool, data: any, isMadeByUser: boolean): boolean {
        if (tool.name !== 'Cursor') {
            this.addOwner(data);
        }

        if (this.beforeDrawCallback) {
            this.beforeDrawCallback(data, isMadeByUser);
        }

        // Draw
        const result = tool.draw(data, isMadeByUser);
        if (result && this.drawCallback) {
            this.drawCallback(tool, data);
            if (tool.name !== 'Cursor') {
                this.applyHooks(this.messageHooks, data);
            }
        }
        return result;
    }

    public send(tool: BoardTool, data: any, isMadeByUser: boolean = true) {
        // Send
        if (this.sendCallback) {
            data.tool = tool.name;
            this.applyHooks(this.messageHooks, data);
            this.sendCallback(tool, data, isMadeByUser);
        }
    }


    public addAnnotationId(annotationId: string, userName: string): void {
        if (annotationId && !this.annotations.includes(annotationId)) {
            this.annotations.push(annotationId);
        }
    }

    public addAnnotationOwners(parent, data) {
        this.annotationOwners[parent.id] = {
            userName: data.userName
        };

        let title = this.createSVGElement('title');
        title.id = 'creator-' + parent.id;
        title.textContent = data && data.userName ? data.userName : 'Undefined';
        parent.setAttribute('aria-labelledby', 'creator-' + parent.id);
        parent.appendChild(title);
    }


    private removeAnnotationId(annotationId: string): void {
        this.annotations = this.annotations.filter((annotation : string) => annotation !== annotationId);
    }

    private addOwner(data) {
        if (!data.id && !data.parent) {
            return;
        }
        if (this.annotationOwners[data.id]) {
            data.userName = this.annotationOwners[data.id].userName;
            return;
        }
        data.userName = this.username;
    }


    changeTool(toolName: string, isSecondary?: boolean) {
        let newTool: BoardTool = this.list[toolName];
        let oldTool = this.curTool;
        if (!newTool) {
            throw new Error(`Trying to select a tool that has never been added! WboBoard[${toolName}]`);
        }

        if (newTool.secondary && isSecondary) {
            newTool.secondary.active = true;

            if (newTool.secondary.switch) {
                newTool.secondary.switch();
            }

            if (newTool === oldTool) {
                return;
            }
        }
        if (newTool.secondary && !isSecondary) {
            newTool.secondary.active = false;
            if (newTool.secondary.switch) {
                newTool.secondary.switch();
            }
        }
        if (!newTool.oneTouch) {
            let mouseCursor = newTool.mouseCursor || 'auto';
            if (mouseCursor.indexOf('url(\'/assets') >= 0) {
                mouseCursor = mouseCursor.replaceAll('url(\'/assets', 'url(\'/wbo/assets');
            }
            this.svg.style.cursor = mouseCursor;

            //There is not necessarily already a curTool
            if (this.curTool) {
                //It's useless to do anything if the new tool is already selected
                if (newTool === this.curTool) return;

                //Remove the old event listeners
                this.removeToolListeners(this.curTool);

                //Call the callbacks of the old tool
                this.curTool.onquit(newTool);
            }

            //Add the new event listeners
            this.addToolListeners(newTool);
            this.curTool = newTool;
        }

        //Call the start callback of the new tool
        newTool.onstart(oldTool);
    }


    public unsetCurrentTool() {
        this.removeToolListeners(this.curTool);
        //Call the callbacks of the old tool
        this.curTool?.onquit();

        this.curTool = null;
        this.svg.style.cursor = 'auto';
    }


    private sendCompleteAnnotationSelfContained(parent: any, children: any[], operationId: number, lastAnnotation: boolean, onDoneCallback: Function) {
        const message = {
            board: this.currentBoard,
            data: {...parent},
            isMadeByUser: false,
            operationId: operationId,
            messageId: ((!children || parent.type === 'delete') && lastAnnotation) ? operationId : undefined,
        }
        message.data._children = [];

        if (children && parent.type !== 'delete') {
            for (let index = 0; index < children.length; index++) {
                const lastChildOfLastAnnotation: boolean = lastAnnotation && (index == children.length - 1);
                const child = children[index];
                child.userName = this.username;
                child.operationId = operationId;
                message.data._children.push(child);
            }

        }

        if (this.broadcastMessage) {
            this.broadcastMessage(message, onDoneCallback);
        }


    }

    private drawCompleteAnnotation(tool: any, parent: any, children: any[]) {
        if (parent.type === 'delete') {
            tool.draw(parent);
            this.removeAnnotationId(parent.id);

        } else {
            this.addAnnotationId(parent.id, parent.userName);
            const isMadeByUser: boolean = false;
            tool.draw({...parent}, isMadeByUser);

            if (children && parent.type !== 'delete') {
                for (let index = 0; index < children.length; index++) {
                    const child: any = { ... children[index], flushPathData: index == children.length - 1};
                    tool.draw(child, isMadeByUser);
                }
            }

            if (parent.deltax != null && parent.deltay != null) {
                this.list['Hand']?.draw({
                    type: 'update',
                    id: parent.id,
                    deltax: parent.deltax,
                    deltay: parent.deltay
                });          
            }
        }
    }

    public drawAndSendSeveralAnnotations(tool: any, parent: any, children: any[], lastAnnotation: boolean, onDoneCallback: Function) {
        const isMadeByUser: boolean = false;
        const sendMessage =  this.canSendMessage();
        const operationId = sendMessage ? Date.now() : undefined;

        parent.userName = parent.userName ?? this.username;
        parent.tool = tool.name;

        this.beforeDrawCallback({ ...parent }, isMadeByUser);

        if (this.persistenceFunction) {
            const parentData = {...parent, _children: children, operationId};
            this.persistenceFunction(parentData, true);
        }

        this.drawCompleteAnnotation(tool, parent, children);

        this.drawCallback(tool, {... parent});

        if (sendMessage) {
            this.sendCompleteAnnotationSelfContained(parent, children, operationId, lastAnnotation, onDoneCallback);

        } else if (lastAnnotation) {
            onDoneCallback();
        }
    }


    public clearDrawingArea() {
        this.drawingArea.innerHTML = '';
    }

    bringToFront(annotationId: string) {
        const annotation = this.drawingArea.querySelector(`#${annotationId}`);
        if (annotation) {
            this.drawingArea.appendChild(annotation);
        }
    }
}
